import type { RequestArchiveBody } from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue.ts";
import {
  invalidateStatusChangeQueries,
  scheduleQueueAutomationRefetch,
} from "@/features/requests/utils/requestInvalidation";
import {
  invalidateQueryKeys,
  projectsKeys,
  projectUsersKeys,
  requestKeys,
} from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import apiClient from "@/lib/api/client";

export function useArchiveRequest() {
  const { data: request } = useGetRequest();
  const requestId = request?.id;
  const queryClient = useQueryClient();

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: RequestArchiveBody) =>
      apiClient.url(`/requests/${requestId}/archive`).post(data).json(),
  });

  const archiveRequest = (
    data: RequestArchiveBody,
    onClose: () => void,
    onHasDeliverable?: () => void,
  ) => {
    mutate(data, {
      onError: (error: unknown) => {
        const status = (error as { status?: number }).status;

        if (status === 400 && onHasDeliverable) {
          onHasDeliverable();

          return;
        }

        toast.add({
          description: "Please try again later",
          timeout: 2000,
          title: "Error archiving request",
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: "The request has been archived.",
          timeout: 2000,
          title: "Request archived",
          type: "success",
        });
        onClose();

        const keys = requestId ? [requestKeys.requestById(requestId)] : [];

        queryClient.invalidateQueries({ queryKey: requestKeys.all });
        queryClient.invalidateQueries({ queryKey: projectUsersKeys.all });
        queryClient.invalidateQueries({ queryKey: projectsKeys.all });
        invalidateQueryKeys(queryClient, keys);
        invalidateStatusChangeQueries(queryClient);
        scheduleQueueAutomationRefetch(queryClient, keys);
      },
    });
  };

  return { archiveRequest, isPending };
}
